import { OptionProps } from 'antd/es/mentions';
import { convertToDecimal } from '@core/utils/methods';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import { ApiMethods } from '@models/enums';
import {
  ContactsData,
  CreateSiteResp,
  MainBrowseFilter,
  PaginatedResponse,
  ProjectDetailsResponse,
  RequestContactsModalData,
  SiteAssociatedProjectsTabData,
  SiteBrowse,
  SiteContactsTabData,
  SiteInformationTabData,
} from '@models/interfaces';
import { FileType } from '@models/types';
import { SiteActions } from '@store/actions';
import { ProjectsEffects } from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class SiteHttpService extends CommonHttpService {
  @StoredRequest(SiteActions.getSiteBrowseDataAction)
  getSiteBrowse(
    params: MainBrowseFilter,
  ): Promise<PaginatedResponse<SiteBrowse>> {
    return this.http.callApi.get(`${this.apiUrl}/sites/browse`, null, {
      params,
    });
  }

  @StoredRequest(SiteActions.getPSLocationCodeOptionsList)
  getPSLocationCodeOptions(): Promise<OptionProps[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/sites/ps-location-code-options-list`,
    );
  }

  @StoredRequest(SiteActions.getMDGLocationCodeOptionsList)
  getMDGLocationCodeOptions(): Promise<OptionProps[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/sites/mdg-location-code-options-list`,
    );
  }

  @StoredRequest(SiteActions.createSiteAction)
  createSite(siteBrowse: SiteBrowse): Promise<CreateSiteResp> {
    return this.http.callApi.post(
      `${this.apiUrl}/sites`,
      SiteHttpService._createUpdateSiteResult(siteBrowse),
    );
  }

  importSites(file: FileType): Promise<void> {
    return this.uploadFile(
      ApiMethods.Post,
      `${this.apiUrl}/sites/import`,
      file,
    ) as Promise<void>;
  }

  importUpdateSites(file: FileType): Promise<void> {
    return this.uploadFile(
      ApiMethods.Patch,
      `${this.apiUrl}/sites/import`,
      file,
    ) as Promise<void>;
  }

  @StoredRequest(SiteActions.updateSiteAction)
  async updateSite(
    siteId: string,
    siteBrowse: SiteBrowse,
  ): Promise<SiteBrowse> {
    const result = SiteHttpService._createUpdateSiteResult(siteBrowse);

    await this.http.callApi.patch(`${this.apiUrl}/sites`, result, {
      params: { siteId },
    });

    return { ...result, id: siteId };
  }

  @StoredRequest(SiteActions.getSiteInformationAction)
  getSiteInformation(
    locationCode: string,
    resetProjectHold = false,
  ): Promise<SiteInformationTabData> {
    if (resetProjectHold) {
      ProjectsEffects.setHoldOptions({} as ProjectDetailsResponse);
    }

    return this.http.callApi.get(
      `${this.apiUrl}/sites/${locationCode}/information`,
      null,
    );
  }

  @StoredRequest(SiteActions.getSiteContactsAction)
  getSiteContacts(locationCode: string): Promise<SiteContactsTabData> {
    return this.http.callApi.get(
      `${this.apiUrl}/sites/${locationCode}/contacts`,
      null,
    );
  }

  @StoredRequest(SiteActions.getSiteAssociatedProjectsAction)
  getSiteAssociatedProjects(
    locationCode: string,
  ): Promise<SiteAssociatedProjectsTabData> {
    return this.http.callApi.get(
      `${this.apiUrl}/sites/${locationCode}/associated-projects`,
      null,
    );
  }

  deleteSite(siteId: string): Promise<void> {
    return this.http.callApi.delete(`${this.apiUrl}/sites`, null, {
      params: { siteId },
    });
  }

  @StoredRequest(SiteActions.createSiteContactAction)
  async createSiteContact(
    siteId: string,
    values: Omit<RequestContactsModalData, 'id'>,
  ): Promise<ContactsData> {
    const { contactType, projectType, state, ...body } = values;

    const id = await this.http.callApi.post(
      `${this.apiUrl}/sites/contacts`,
      {
        ...body,
        state: state?.id,
        projectType: projectType?.id,
        contactType: contactType?.id,
      },
      {
        params: {
          siteId,
        },
      },
    );

    return {
      id,
      ...values,
    };
  }

  async validateSiteUniqueField({
    PSLocationCode,
    MDGLocationCode,
    id,
  }: {
    PSLocationCode?: string;
    MDGLocationCode?: string;
    id?: string;
  }): Promise<boolean> {
    return this.http.callApi.get(`${this.apiUrl}/sites/validation`, null, {
      params: { PSLocationCode, MDGLocationCode, id },
    });
  }

  @StoredRequest(SiteActions.updateSiteContactAction)
  async updateSiteContact(
    values: RequestContactsModalData,
  ): Promise<RequestContactsModalData> {
    const { contactType, projectType, state, id, ...body } = values;

    await this.http.callApi.patch(
      `${this.apiUrl}/sites/contacts`,
      {
        ...body,
        state: state?.id ? state.id : null,
        projectType: projectType?.id,
        contactType: contactType?.id,
      },
      {
        params: { contactId: id },
      },
    );

    return {
      ...values,
    };
  }

  @StoredRequest(SiteActions.deleteSiteContactAction)
  async deleteSiteContact(contactId: string): Promise<string> {
    await this.http.callApi.delete(`${this.apiUrl}/sites/contacts`, null, {
      params: { contactId },
    });

    return contactId;
  }

  private static _createUpdateSiteResult({
    latitude,
    longitude,
    ...body
  }: SiteBrowse): SiteBrowse {
    return {
      ...body,
      ...(latitude ? { latitude: convertToDecimal(`${latitude}`) } : {}),
      ...(longitude ? { longitude: convertToDecimal(`${longitude}`) } : {}),
    };
  }
}
